import React, { useMemo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function getIntensity(count) {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  return 3;
}

export default function ActivityHeatmap({ data = [], year }) {
  const theme = useTheme();

  const COLORS = [
    theme.palette.action.disabledBackground,
    theme.palette.success.light,
    theme.palette.success.main,
    theme.palette.success.dark,
  ];

  // Build week columns: each column is 7 days (Sun–Sat)
  const weeks = useMemo(() => {
    if (!data.length) return [];
    const jan1 = new Date(`${year}-01-01T00:00:00.000Z`);
    const startDow = jan1.getUTCDay(); // 0=Sun
    const paddedDays = Array(startDow).fill(null).concat(data);
    const result = [];
    for (let i = 0; i < paddedDays.length; i += 7) {
      result.push(paddedDays.slice(i, i + 7));
    }
    return result;
  }, [data, year]);

  const monthLabels = useMemo(() => {
    const labels = [];
    let lastMonth = -1;
    weeks.forEach((week, wi) => {
      week.forEach(day => {
        if (!day) return;
        const m = new Date(day.date).getUTCMonth();
        if (m !== lastMonth) { labels.push({ month: m, col: wi }); lastMonth = m; }
      });
    });
    return labels;
  }, [weeks]);

  if (!data.length) return null;

  return (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      {/* Month labels */}
      <Box sx={{ display: 'flex', ml: '28px', mb: 0.5 }}>
        {weeks.map((_, wi) => {
          const label = monthLabels.find(l => l.col === wi);
          return (
            <Box key={wi} sx={{ width: 12, mr: '2px', flexShrink: 0 }}>
              {label && (
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {MONTH_NAMES[label.month]}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex' }}>
        {/* Day-of-week labels */}
        <Box sx={{ display: 'flex', flexDirection: 'column', mr: '4px', width: 24, flexShrink: 0 }}>
          {DAY_NAMES.map((d, i) => (
            <Box key={d} sx={{ height: 12, mb: '2px', display: 'flex', alignItems: 'center' }}>
              {i % 2 === 1 && (
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', lineHeight: 1 }}>
                  {d}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* Week columns */}
        <Box sx={{ display: 'flex', gap: '2px' }}>
          {weeks.map((week, wi) => (
            <Box key={wi} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {Array.from({ length: 7 }, (_, di) => {
                const day = week[di];
                if (!day) return <Box key={di} sx={{ width: 10, height: 10 }} />;
                const intensity = getIntensity(day.count);
                const d = new Date(day.date + 'T00:00:00.000Z');
                const label = `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} — ${day.count} action${day.count !== 1 ? 's' : ''}`;
                return (
                  <Tooltip key={di} title={label} arrow>
                    <Box
                      sx={{
                        width: 10, height: 10,
                        borderRadius: '2px',
                        bgcolor: COLORS[intensity],
                        cursor: 'default',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, ml: '28px' }}>
        <Typography variant="caption" color="text.secondary">Less</Typography>
        {COLORS.map((c, i) => (
          <Box key={i} sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: c }} />
        ))}
        <Typography variant="caption" color="text.secondary">More</Typography>
      </Box>
    </Box>
  );
}
